import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Chip,
  Divider,
  Alert,
  CircularProgress
} from '@mui/material';
import { Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';

const Investment_Strategy = () => {
  const [strategy, setStrategy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({
    strategy_name: '',
    investment_strategy: ''
  });

  useEffect(() => {
    fetchStrategy();
  }, []);

  const fetchStrategy = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('userId') || '2'; // Default to user 2 for now
      
      const response = await fetch(`/api/investment-strategy/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch strategy');
      }

      const data = await response.json();
      setStrategy(data);
      setEditData({
        strategy_name: data.strategy_name || '',
        investment_strategy: data.investment_strategy || ''
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setEditing(true);
  };

  const handleCancel = () => {
    setEditing(false);
    setEditData({
      strategy_name: strategy.strategy_name || '',
      investment_strategy: strategy.investment_strategy || ''
    });
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('userId') || '2';
      
      const response = await fetch(`/api/investment-strategy/${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editData)
      });

      if (!response.ok) {
        throw new Error('Failed to update strategy');
      }

      const updatedData = await response.json();
      setStrategy(updatedData);
      setEditing(false);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!strategy) {
    return (
      <Alert severity="info">
        No investment strategy found. Please create one.
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Investment Strategy
      </Typography>

      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Strategy Name
            </Typography>
            {editing ? (
              <TextField
                fullWidth
                value={editData.strategy_name}
                onChange={(e) => handleInputChange('strategy_name', e.target.value)}
                variant="outlined"
                size="small"
              />
            ) : (
              <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                {strategy.strategy_name}
              </Typography>
            )}
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Status
            </Typography>
            <Chip 
              label={strategy.is_active ? 'Active' : 'Inactive'} 
              color={strategy.is_active ? 'success' : 'default'}
              size="small"
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Investment Strategy
          </Typography>
          {editing ? (
            <TextField
              fullWidth
              multiline
              rows={20}
              value={editData.investment_strategy}
              onChange={(e) => handleInputChange('investment_strategy', e.target.value)}
              variant="outlined"
              sx={{ fontFamily: 'monospace' }}
            />
          ) : (
            <>
              {/* DAS-3359: reading shows today's (D) figures; Edit shows the {{placeholders}} */}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Figures marked (D) show today's values. Edit shows the placeholders the Dashboard fills.
              </Typography>
              <Paper
                variant="outlined"
                data-testid="strategy-text-filled"
                sx={{
                  p: 2,
                  backgroundColor: '#f8f9fa',
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.6,
                  fontSize: '14px'
                }}
              >
                {strategy.investment_strategy_filled || strategy.investment_strategy}
              </Paper>
            </>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Created
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {new Date(strategy.created_at).toLocaleString()}
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Last Updated
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {new Date(strategy.updated_at).toLocaleString()}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        {editing ? (
          <>
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={loading}
            >
              Save Changes
            </Button>
          </>
        ) : (
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={handleEdit}
          >
            Edit Strategy
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default Investment_Strategy;
